import { useId } from 'react';
import { Box } from '@mui/material';
import { PLAIN } from './measurePaint';

/**
 * The client's own photo, with one measurement drawn on it.
 *
 * Alignment is the first job here. Landmarks arrive in NORMALISED coordinates
 * (0–1 across the frame), so the overlay must cover exactly the rectangle the
 * image occupies, with no cropping and no letterboxing:
 *
 *   - the container takes the FRAME'S own aspect ratio, from `imageSize`
 *   - the image fills it (`object-fit: fill`), which is lossless because the
 *     box already has the image's proportions
 *   - the SVG viewBox is the frame's PIXEL space, and the normalised points
 *     are multiplied up into it
 *
 * Two shortcuts were rejected, both of which look right only on a square
 * capture. A square box with `object-fit: cover` crops the photo while the
 * overlay stretches to the box, so every point drifts off its feature. And a
 * `0 0 1 1` viewBox makes radii and stroke widths fractions of the whole
 * image — a dot of r=0.01 on a 720×960 frame is an ellipse, not a circle,
 * because the two axes scale differently.
 *
 * Working in pixel space solves both: the box and the viewBox share one ratio,
 * so the mapping is a uniform scale, circles stay circular, and a dot sized
 * against the frame looks the same on every capture.
 *
 * The second job is PROMINENCE. One measurement is on screen at a time, and it
 * should be unmistakably the subject of the picture:
 *
 *   colour      every point and line is drawn in the colour the paint layer
 *               gives it, so the drawing carries what the measurement found and
 *               not merely where it looked. See `measurePaint.js`.
 *   spotlight   everything outside the measured area is dimmed, with a soft
 *               edge, so the eye lands on the finding rather than on the face.
 *   glow        a wide translucent pass under each stroke in its own colour,
 *               which is what keeps a thin line readable over hair and shadow.
 *   draw-in     lines draw themselves and points arrive after them, so the
 *               client's eye follows the measurement being taken. Silent for
 *               anyone who has asked for less motion.
 *
 * What it still does NOT do is interpret. Nothing is drawn that the agent did
 * not record, no verdict is written on anyone's face, and the colours come
 * from the agent's own figures or from nothing at all.
 */

const INK = '#11223E';        // navy underlay, so the line reads on pale skin
// How far the rest of the photo steps back behind the measurement. Enough to
// make the finding the subject of the picture, not so much that the face
// disappears — this is emphasis, not a mask.
const WASH = 0.34;

export default function FaceCanvas({ frame, measure, paint, dim = false }) {
  // Stable per instance: two canvases on one page must not share a mask id.
  const uid = useId().replace(/[^\w-]/g, '');

  if (!frame) return null;

  const size = frame.imageSize || {};
  const w = Number(size.width) > 0 ? Number(size.width) : 720;
  const h = Number(size.height) > 0 ? Number(size.height) : 720;
  const lm = frame.landmarks || [];

  // Sized against the frame itself, so a dot is the same visual size whatever
  // resolution the capture came in at.
  const unit = Math.max(w, h);
  const dot = unit * 0.009;
  const stroke = unit * 0.005;

  const at = (i) => {
    const p = lm[i];
    if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return null;
    return { x: p.x * w, y: p.y * h };
  };

  // The paint layer decides the colours; without it (or for a point it says
  // nothing about) the drawing falls back to the single candlelight stroke,
  // which is exactly what this looked like before colour meant anything.
  const points = paint?.points?.length
    ? paint.points
    : (measure?.points || []).map((index) => ({ index, color: PLAIN }));
  const lines = paint?.lines?.length
    ? paint.lines
    : (measure?.lines || [])
      .filter((l) => Array.isArray(l) && l.length >= 2)
      .map(([from, to]) => ({ from, to, color: PLAIN }));

  // The area the measurement is actually about: the points it is read around,
  // ignoring any that the paint layer has stepped back as context. Used for
  // the spotlight only — nothing is cropped or hidden.
  const focal = points.filter((p) => !p.muted).map((p) => at(p.index)).filter(Boolean);
  const spot = focal.length ? (() => {
    const xs = focal.map((p) => p.x); const ys = focal.map((p) => p.y);
    const pad = unit * 0.07;
    const box = {
      x: Math.min(...xs) - pad, y: Math.min(...ys) - pad,
      width: Math.max(...xs) - Math.min(...xs) + pad * 2,
      height: Math.max(...ys) - Math.min(...ys) + pad * 2,
    };
    // A floor on each axis. The facial thirds run down the midline, so their
    // own bounding box is a few pixels wide — spotlighting exactly that leaves
    // a stripe and no face. The client has to still recognise themselves.
    const grow = (start, length, min) => (length >= min
      ? [start, length]
      : [start + length / 2 - min / 2, min]);
    const [x, width] = grow(box.x, box.width, w * 0.45);
    const [y, height] = grow(box.y, box.height, h * 0.45);
    return { x, y, width, height };
  })() : null;

  // Remounts the drawing when the measurement or the photo changes, so the
  // draw-in plays again instead of only on first paint.
  const take = `${measure?.key || 'none'}|${frame.pose || ''}`;

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      // The frame's OWN proportions. Forcing a square here is what pushes the
      // drawing off the face on a portrait capture.
      aspectRatio: `${w} / ${h}`,
      borderRadius: 3,
      overflow: 'hidden',
      bgcolor: '#0A1425',
      border: '1px solid',
      borderColor: 'rgba(192,167,134,.35)',
    }}>
      {frame.image && (
        <Box
          component="img"
          src={frame.image}
          alt=""
          draggable={false}
          sx={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            // `fill` is exact here BECAUSE the box already has the image's
            // ratio; it never distorts and never crops.
            objectFit: 'fill',
            display: 'block',
            filter: dim ? 'saturate(.85) brightness(.92)' : 'none',
            transition: 'filter .25s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      )}

      <Box
        component="svg"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        // Names the overlay so alignment can be checked against the image it
        // sits on; every icon on the page is also an <svg>.
        data-face-overlay="true"
        sx={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          // Motion is an invitation to follow the measurement, never a
          // requirement: without it everything is already in its final state.
          '@media (prefers-reduced-motion: no-preference)': {
            '@keyframes candleDraw': { from: { strokeDashoffset: 1 }, to: { strokeDashoffset: 0 } },
            '@keyframes candlePop': {
              from: { transform: 'scale(.3)', opacity: 0 },
              to: { transform: 'scale(1)', opacity: 1 },
            },
            // Ends ON the wash's own opacity, not on 1. A filled animation
            // overrides the element's `opacity` attribute for good, so a
            // `to: { opacity: 1 }` here would black out everything outside
            // the measurement instead of gently stepping it back.
            '@keyframes candleSpot': { from: { opacity: 0 }, to: { opacity: WASH } },
            '& [data-draw]': {
              // pathLength normalises every line's length to 1, so one set of
              // keyframes draws a short line and a long one at the same speed.
              animation: 'candleDraw .5s ease-out both',
            },
            '& [data-pop]': {
              transformBox: 'fill-box', transformOrigin: 'center',
              animation: 'candlePop .32s cubic-bezier(.34,1.56,.64,1) both',
            },
            '& [data-spot]': { animation: 'candleSpot .4s ease both' },
          },
        }}
      >
        <g key={take}>
          {/* ORDER MATTERS. Context first, then the wash that pushes it back,
              then the measurement on top of both — so a point the agent
              recorded but this measure is not about recedes with the rest of
              the face instead of floating brightly over a darkened photo. */}
          {lines.filter((l) => l.muted).map((l, i) => {
            const p1 = at(l.from); const p2 = at(l.to);
            if (!p1 || !p2) return null;
            return (
              <line
                key={`ml${l.from}-${l.to}-${i}`}
                x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke={l.color || PLAIN} strokeOpacity={0.55}
                strokeWidth={stroke * 0.7} strokeLinecap="round"
              />
            );
          })}
          {points.filter((pt) => pt.muted).map((pt, i) => {
            const p = at(pt.index);
            if (!p) return null;
            return (
              <circle key={`mp${pt.index}-${i}`} cx={p.x} cy={p.y} r={dot * 0.5}
                fill={pt.color || PLAIN} fillOpacity={0.65} />
            );
          })}

          {/* Everything that is not the measurement steps back. A soft-edged
              hole rather than a hard one: a crisp rectangle over someone's
              face reads as a crop, which this is not. Light enough that the
              face is still a face out there — this is emphasis, not a mask. */}
          {spot && (
            <>
              <defs>
                <filter id={`soft-${uid}`}>
                  <feGaussianBlur stdDeviation={unit * 0.045} />
                </filter>
                <mask id={`spot-${uid}`}>
                  <rect x="0" y="0" width={w} height={h} fill="#FFFFFF" />
                  <rect
                    x={spot.x} y={spot.y} width={spot.width} height={spot.height}
                    rx={unit * 0.06} fill="#000000" filter={`url(#soft-${uid})`}
                  />
                </mask>
              </defs>
              <rect
                data-spot x="0" y="0" width={w} height={h}
                fill="#0A1425" opacity={WASH} mask={`url(#spot-${uid})`}
              />
            </>
          )}

          {lines.filter((l) => !l.muted).map((l, i) => {
            const p1 = at(l.from); const p2 = at(l.to);
            if (!p1 || !p2) return null;
            const color = l.color || PLAIN;
            const geom = { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
            return (
              <g key={`l${l.from}-${l.to}-${i}`}>
                {/* The glow carries the colour outward so the stroke survives
                    being drawn over hair, shadow and pale skin alike. */}
                <line {...geom} stroke={color} strokeOpacity={0.28}
                  strokeWidth={stroke * 3.4} strokeLinecap="round" />
                <line {...geom} stroke={INK} strokeOpacity={0.55}
                  strokeWidth={stroke * 2} strokeLinecap="round" />
                <line
                  {...geom} data-draw pathLength={1} strokeDasharray={1}
                  style={{ animationDelay: `${Math.min(i, 12) * 0.045}s` }}
                  stroke={color} strokeWidth={stroke * 1.15} strokeLinecap="round"
                />
              </g>
            );
          })}

          {points.filter((pt) => !pt.muted).map((pt, i) => {
            const p = at(pt.index);
            if (!p) return null;
            const color = pt.color || PLAIN;
            return (
              <g key={`p${pt.index}-${i}`} data-pop
                style={{ animationDelay: `${0.2 + Math.min(i, 16) * 0.025}s` }}>
                <circle cx={p.x} cy={p.y} r={dot * 2.5} fill={color} fillOpacity={0.2} />
                <circle cx={p.x} cy={p.y} r={dot * 1.75} fill={INK} fillOpacity={0.72} />
                <circle cx={p.x} cy={p.y} r={dot} fill={color}
                  stroke="#FFFFFF" strokeOpacity={0.92} strokeWidth={stroke * 0.5} />
              </g>
            );
          })}
        </g>
      </Box>
    </Box>
  );
}
